/**
 * Country code helpers used by the area dialog's zip-range editor.
 *
 *   - `resolveCountryIso3` accepts whatever the geocoder hands back — ISO-3, ISO-2, or a
 *     country name in any locale — and returns the canonical ISO-3 code the backend stores
 *     against an area's zip ranges (`^[A-Z]{3}$`).
 *   - `listCountryOptions` produces a sorted, locale-aware option list for the country
 *     dropdown. Names come from the browser's `Intl.DisplayNames`; we map the platform's
 *     ISO-2 universe through the table below to get the ISO-3 codes the backend expects.
 *
 * The alpha-2 → alpha-3 table is exhaustive against ISO 3166-1; keeping it inline (rather
 * than pulling a 5 KB JSON) means the bundle ships the same payload it would otherwise but
 * with zero load-time fetch.
 */
const ISO2_TO_ISO3: Readonly<Record<string, string>> = {
  AD: 'AND', AE: 'ARE', AF: 'AFG', AG: 'ATG', AI: 'AIA', AL: 'ALB', AM: 'ARM', AO: 'AGO',
  AQ: 'ATA', AR: 'ARG', AS: 'ASM', AT: 'AUT', AU: 'AUS', AW: 'ABW', AX: 'ALA', AZ: 'AZE',
  BA: 'BIH', BB: 'BRB', BD: 'BGD', BE: 'BEL', BF: 'BFA', BG: 'BGR', BH: 'BHR', BI: 'BDI',
  BJ: 'BEN', BL: 'BLM', BM: 'BMU', BN: 'BRN', BO: 'BOL', BQ: 'BES', BR: 'BRA', BS: 'BHS',
  BT: 'BTN', BV: 'BVT', BW: 'BWA', BY: 'BLR', BZ: 'BLZ',
  CA: 'CAN', CC: 'CCK', CD: 'COD', CF: 'CAF', CG: 'COG', CH: 'CHE', CI: 'CIV', CK: 'COK',
  CL: 'CHL', CM: 'CMR', CN: 'CHN', CO: 'COL', CR: 'CRI', CU: 'CUB', CV: 'CPV', CW: 'CUW',
  CX: 'CXR', CY: 'CYP', CZ: 'CZE',
  DE: 'DEU', DJ: 'DJI', DK: 'DNK', DM: 'DMA', DO: 'DOM', DZ: 'DZA',
  EC: 'ECU', EE: 'EST', EG: 'EGY', EH: 'ESH', ER: 'ERI', ES: 'ESP', ET: 'ETH',
  FI: 'FIN', FJ: 'FJI', FK: 'FLK', FM: 'FSM', FO: 'FRO', FR: 'FRA',
  GA: 'GAB', GB: 'GBR', GD: 'GRD', GE: 'GEO', GF: 'GUF', GG: 'GGY', GH: 'GHA', GI: 'GIB',
  GL: 'GRL', GM: 'GMB', GN: 'GIN', GP: 'GLP', GQ: 'GNQ', GR: 'GRC', GS: 'SGS', GT: 'GTM',
  GU: 'GUM', GW: 'GNB', GY: 'GUY',
  HK: 'HKG', HM: 'HMD', HN: 'HND', HR: 'HRV', HT: 'HTI', HU: 'HUN',
  ID: 'IDN', IE: 'IRL', IL: 'ISR', IM: 'IMN', IN: 'IND', IO: 'IOT', IQ: 'IRQ', IR: 'IRN',
  IS: 'ISL', IT: 'ITA',
  JE: 'JEY', JM: 'JAM', JO: 'JOR', JP: 'JPN',
  KE: 'KEN', KG: 'KGZ', KH: 'KHM', KI: 'KIR', KM: 'COM', KN: 'KNA', KP: 'PRK', KR: 'KOR',
  KW: 'KWT', KY: 'CYM', KZ: 'KAZ',
  LA: 'LAO', LB: 'LBN', LC: 'LCA', LI: 'LIE', LK: 'LKA', LR: 'LBR', LS: 'LSO', LT: 'LTU',
  LU: 'LUX', LV: 'LVA', LY: 'LBY',
  MA: 'MAR', MC: 'MCO', MD: 'MDA', ME: 'MNE', MF: 'MAF', MG: 'MDG', MH: 'MHL', MK: 'MKD',
  ML: 'MLI', MM: 'MMR', MN: 'MNG', MO: 'MAC', MP: 'MNP', MQ: 'MTQ', MR: 'MRT', MS: 'MSR',
  MT: 'MLT', MU: 'MUS', MV: 'MDV', MW: 'MWI', MX: 'MEX', MY: 'MYS', MZ: 'MOZ',
  NA: 'NAM', NC: 'NCL', NE: 'NER', NF: 'NFK', NG: 'NGA', NI: 'NIC', NL: 'NLD', NO: 'NOR',
  NP: 'NPL', NR: 'NRU', NU: 'NIU', NZ: 'NZL',
  OM: 'OMN',
  PA: 'PAN', PE: 'PER', PF: 'PYF', PG: 'PNG', PH: 'PHL', PK: 'PAK', PL: 'POL', PM: 'SPM',
  PN: 'PCN', PR: 'PRI', PS: 'PSE', PT: 'PRT', PW: 'PLW', PY: 'PRY',
  QA: 'QAT',
  RE: 'REU', RO: 'ROU', RS: 'SRB', RU: 'RUS', RW: 'RWA',
  SA: 'SAU', SB: 'SLB', SC: 'SYC', SD: 'SDN', SE: 'SWE', SG: 'SGP', SH: 'SHN', SI: 'SVN',
  SJ: 'SJM', SK: 'SVK', SL: 'SLE', SM: 'SMR', SN: 'SEN', SO: 'SOM', SR: 'SUR', SS: 'SSD',
  ST: 'STP', SV: 'SLV', SX: 'SXM', SY: 'SYR', SZ: 'SWZ',
  TC: 'TCA', TD: 'TCD', TF: 'ATF', TG: 'TGO', TH: 'THA', TJ: 'TJK', TK: 'TKL', TL: 'TLS',
  TM: 'TKM', TN: 'TUN', TO: 'TON', TR: 'TUR', TT: 'TTO', TV: 'TUV', TW: 'TWN', TZ: 'TZA',
  UA: 'UKR', UG: 'UGA', UM: 'UMI', US: 'USA', UY: 'URY', UZ: 'UZB',
  VA: 'VAT', VC: 'VCT', VE: 'VEN', VG: 'VGB', VI: 'VIR', VN: 'VNM', VU: 'VUT',
  WF: 'WLF', WS: 'WSM',
  YE: 'YEM', YT: 'MYT',
  ZA: 'ZAF', ZM: 'ZMB', ZW: 'ZWE',
};

/** Reverse map for quick ISO-3 → ISO-2 lookup when feeding `Intl.DisplayNames`. */
const ISO3_TO_ISO2: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(ISO2_TO_ISO3).map(([two, three]) => [three, two]),
);

/**
 * Resolve any of:
 *   - an ISO-3 code ("AUT") — passed through unchanged after normalisation,
 *   - an ISO-2 code ("AT") — translated via ISO2_TO_ISO3,
 *   - a country name in any locale ("Austria", "Österreich", "Autriche") — looked up via
 *     `Intl.DisplayNames` against every ISO-2 code in the platform's universe,
 * to a canonical ISO-3 code. Returns `null` when nothing matches.
 */
export function resolveCountryIso3(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  if (/^[A-Za-z]{3}$/.test(trimmed)) {
    const upper = trimmed.toUpperCase();
    return ISO3_TO_ISO2[upper] ? upper : null;
  }
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return ISO2_TO_ISO3[trimmed.toUpperCase()] ?? null;
  }
  return resolveByName(trimmed);
}

/**
 * Try to match the input against country names produced by `Intl.DisplayNames`. We check
 * a handful of likely locales (en/de/fr/it + the language portion of the browser default)
 * so the geocoder's localised string lands the right ISO-3 even if it's not English.
 */
function resolveByName(input: string): string | null {
  const target = input.toLowerCase();
  const locales = ['en', 'de', 'fr', 'it', 'es', getNavigatorLanguage()].filter(
    (locale): locale is string => Boolean(locale),
  );
  const codes = Object.keys(ISO2_TO_ISO3);
  for (const locale of locales) {
    let display: Intl.DisplayNames;
    try {
      display = new Intl.DisplayNames([locale], { type: 'region' });
    } catch {
      continue;
    }
    for (const iso2 of codes) {
      const name = display.of(iso2);
      if (name && name.toLowerCase() === target) {
        return ISO2_TO_ISO3[iso2] ?? null;
      }
    }
  }
  return null;
}

function getNavigatorLanguage(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return navigator.language?.split('-')[0];
}

/**
 * Localised option list for the country dropdown. Each option carries the ISO-3 code we
 * persist and a display label rendered in `locale`. Sorted alphabetically by label using
 * the same locale's collator so the order matches what the user sees.
 *
 * Falls back gracefully when `Intl.DisplayNames` isn't available or rejects the locale:
 * options use the ISO-3 code as the label instead of going blank.
 */
export interface CountryOption {
  readonly iso3: string;
  readonly label: string;
}

export function listCountryOptions(locale: string): readonly CountryOption[] {
  let display: Intl.DisplayNames | null = null;
  try {
    display = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    display = null;
  }
  const options: CountryOption[] = [];
  for (const [iso2, iso3] of Object.entries(ISO2_TO_ISO3)) {
    const localised = display?.of(iso2);
    options.push({ iso3, label: localised ?? iso3 });
  }
  const collator = (() => {
    try {
      return new Intl.Collator(locale);
    } catch {
      return null;
    }
  })();
  options.sort((a, b) =>
    collator ? collator.compare(a.label, b.label) : a.label.localeCompare(b.label),
  );
  return options;
}

/**
 * Localised display name for a single ISO-3 code; used by the cards/table to render
 * stored zip-range countries as "Austria" / "Österreich" rather than raw "AUT". Falls
 * back to the code itself when the name can't be resolved.
 */
export function displayNameForIso3(iso3: string, locale: string): string {
  const upper = iso3.toUpperCase();
  const iso2 = ISO3_TO_ISO2[upper];
  if (!iso2) return upper;
  try {
    const display = new Intl.DisplayNames([locale], { type: 'region' });
    return display.of(iso2) ?? upper;
  } catch {
    return upper;
  }
}
